import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import useConfiguratorStore from '../store/configuratorStore';
import useAuthStore from '../store/authStore';
import { supabase } from '../utils/supabase';
import { getProductImageSrc } from '../utils/imageUtils';
import LazyImage from './shared/LazyImage';
import DiscountSection from './DiscountSection';

const PreviewContainer = styled(motion.div)`
  width: 42%;
  height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  padding-top: 64px;
  position: relative;
  overflow-y: auto;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    min-height: 60vh;
    border-top: 1px solid var(--gray-200);
    padding-top: 0;
  }
`;

// Success Notification Components
const SuccessNotification = styled(motion.div)`
  position: fixed;
  top: 80px;
  right: 2rem;
  background: linear-gradient(135deg, var(--partner-color) 0%, var(--partner-dark) 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px var(--partner-light);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    right: 1rem;
    left: 1rem;
    top: 70px;
  }
`;

const SuccessIcon = styled.div`
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SuccessContent = styled.div`
  flex: 1;
`;

const SuccessTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const SuccessDetails = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
`;

// Optimized Price Mode Toggle Components
const OptimizedPricingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  margin: 0.75rem 0.75rem;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const PricingLabel = styled.div`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
  letter-spacing: -0.025em;
`;

const CompactPriceModeToggle = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 6px;
  padding: 2px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: auto;
  width: fit-content;
`;

const CompactModeOption = styled.button`
  padding: 0.4rem 0.7rem;
  font-size: var(--text-xs);
  font-weight: 600;
  border-radius: 5px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  position: relative;
  letter-spacing: 0.25px;
  
  ${props => props.active ? `
    background: ${props.isPartner ? 'var(--partner-color)' : 'var(--client-color)'};
    color: white;
    box-shadow: 0 2px 4px ${props.isPartner ? 'var(--partner-light)' : 'var(--client-light)'};
    transform: translateY(-0.5px);
  ` : `
    background: transparent;
    color: var(--gray-600);
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: var(--gray-800);
      transform: translateY(-0.5px);
    }
  `}
`;

// Incognito Mode Toggle Components
const IncognitoToggleContainer = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 6px;
  padding: 2px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: auto;
  width: fit-content;
  transition: all 0.3s ease;
  overflow: hidden;
  
  ${props => props.collapsed && `
    width: 36px;
    height: 32px;
    padding: 0;
  `}
`;

const IncognitoButton = styled.button`
  padding: ${props => props.collapsed ? '0.4rem' : '0.4rem 0.7rem'};
  font-size: var(--text-xs);
  font-weight: 600;
  border-radius: 5px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  position: relative;
  letter-spacing: 0.25px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  white-space: nowrap;
  
  ${props => props.active ? `
    background: linear-gradient(135deg, var(--secondary-color) 0%, #0F1D36 100%);
    color: white;
    box-shadow: 0 2px 4px rgba(22, 40, 69, 0.25);
    transform: translateY(-0.5px);
  ` : `
    background: transparent;
    color: var(--gray-600);
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: var(--gray-800);
      transform: translateY(-0.5px);
    }
  `}
`;

const ExpandableToggle = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 2px;
  overflow: hidden;
`;

const IncognitoModeOption = styled.button`
  padding: 0.4rem 0.7rem;
  font-size: var(--text-xs);
  font-weight: 600;
  border-radius: 5px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  position: relative;
  letter-spacing: 0.25px;
  white-space: nowrap;
  
  ${props => {
    if (props.active && props.mode === 'incognito') {
      return `
        background: linear-gradient(135deg, var(--secondary-color) 0%, #0F1D36 100%);
        color: white;
        box-shadow: 0 2px 4px rgba(22, 40, 69, 0.25);
        transform: translateY(-0.5px);
      `;
    } else if (props.active && props.mode === 'partner') {
      return `
        background: var(--partner-color);
        color: white;
        box-shadow: 0 2px 4px var(--partner-light);
        transform: translateY(-0.5px);
      `;
    } else if (props.active && props.mode === 'client') {
      return `
        background: var(--client-color);
        color: white;
        box-shadow: 0 2px 4px var(--client-light);
        transform: translateY(-0.5px);
      `;
    } else {
      return `
        background: transparent;
        color: var(--gray-600);
        
        &:hover {
          background: rgba(0, 0, 0, 0.04);
          color: var(--gray-800);
          transform: translateY(-0.5px);
        }
      `;
    }
  }}
`;

const QuoteContainer = styled.div`
  flex: 1;
  padding: 1rem 1.5rem;
  padding-top: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const QuoteCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: auto;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border-color: #cbd5e1;
  }
`;

const QuoteHeader = styled.div`
  background: var(--secondary-color);
  padding: 1.5rem 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
`;

const QuoteTitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const QuoteIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const QuoteTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: white;
  letter-spacing: -0.01em;
`;

const QuoteReference = styled.span`
  font-size: var(--text-xs);
  color: white;
  background: #FF8133;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  display: inline-block;
  
  &:hover {
    background: #F57C00;
  }
`;

const ProductDetails = styled.div`
  width: 100%;
`;

const ProductItem = styled(motion.div)`
  padding: 1.5rem;
  transition: all 0.2s ease;
  margin: 0.75rem 0.75rem 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;
  }
  
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ProductName = styled.h4`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--gray-800);
`;

const ProductPrice = styled.div`
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--gray-900);
`;

const ProductMeta = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow items to wrap */
  gap: 0.4rem; /* Reduced gap */
  margin-bottom: 1rem;
  position: relative;
  align-items: center;
  /* overflow: visible; // Can be removed if wrapping handles it */
  padding: 6px 0;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    /* overflow-x: auto; // Removed as items will wrap */
    padding-bottom: 8px; /* Keep for spacing if items wrap */
    
    /* Scrollbar styles removed as overflow-x is removed */
  }
`;

const MetaItem = styled.div`
  font-size: 0.7rem; /* Reduced font size */
  color: var(--gray-700);
  background: rgba(255, 255, 255, 0.9);
  padding: 0.2rem 0.4rem; /* Reduced padding */
  border-radius: 6px; /* Slightly smaller radius */
  display: flex;
  align-items: center;
  gap: 0.2rem; /* Reduced gap */
  font-weight: 500;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(52, 152, 219, 0.12);
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
  order: 1; /* Default order */
  z-index: 1;
  margin-bottom: 0.3rem; /* Add some space if items wrap */
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
    border-color: rgba(52, 152, 219, 0.2);
    z-index: 2;
  }
  
  &:nth-of-type(2) {
    order: 2; /* Category badge */
  }
`;

const QuantityTag = styled(MetaItem)`
  background: rgba(248, 250, 252, 0.9);
  color: var(--gray-700);
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  order: 4; /* Unit price badge - after calculated length badge */
  margin-bottom: 0.3rem; /* Add some space if items wrap */
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(0, 0, 0, 0.12);
    color: var(--gray-800);
  }
`;

// Price Mode Badge Component
const PriceModeBadge = styled.span`
  font-size: 0.7rem; /* Reduced font size */
  font-weight: 600;
  padding: 0.2rem 0.4rem; /* Reduced padding */
  border-radius: 6px; /* Slightly smaller radius */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
  order: 4; /* Price mode badge */
  z-index: 1;
  margin-bottom: 0.3rem; /* Add some space if items wrap */
  
  ${props => props.mode === 'client' ? `
    background: linear-gradient(135deg, var(--client-color) 0%, var(--client-dark) 100%);
    color: white;
    border: none;
  ` : `
    background: linear-gradient(135deg, var(--partner-color) 0%, var(--partner-dark) 100%);
    color: white;
    border: none;
  `}
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
    z-index: 2;
    ${props => props.mode === 'client' ? `
      background: linear-gradient(135deg, var(--client-dark) 0%, #c02020 100%);
      box-shadow: 0 4px 8px var(--client-light);
    ` : `
      background: linear-gradient(135deg, var(--partner-dark) 0%, #047857 100%);
      box-shadow: 0 4px 8px var(--partner-light);
    `}
  }
`;

// Calculated Length Badge Component
const CalculatedLengthBadge = styled(MetaItem)`
  background: linear-gradient(135deg, var(--partner-color) 0%, var(--partner-dark) 100%);
  color: white;
  border-color: var(--partner-light);
  box-shadow: 0 2px 6px var(--partner-light);
  order: 3; /* Position after category badge, before quantity badge */
  
  &:hover {
    background: linear-gradient(135deg, var(--partner-dark) 0%, #047857 100%); /* Darker green gradient on hover */
    color: white; /* Maintain white text */
    transform: translateY(-2px);
    box-shadow: 0 4px 8px var(--partner-light);
    border-color: var(--partner-light);
    z-index: 2;
  }
`;
 
const RemoveButton = styled.button`
  font-size: 0.65rem; /* Reduced font size for icon */
  color: var(--gray-600);
  background: white;
  padding: 0.2rem; /* Reduced padding */
  border-radius: 6px; /* Slightly smaller radius */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  width: 24px; /* Reduced width */
  height: 24px; /* Reduced height */
  flex-shrink: 0;
  order: 5; /* Remove button - last */
  z-index: 1;
  margin-bottom: 0.3rem; /* Add some space if items wrap */
  
  &:hover {
    background: rgba(255, 245, 245, 0.9);
    color: #cc0000;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(204, 0, 0, 0.08);
    border-color: rgba(204, 0, 0, 0.15);
    z-index: 2;
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const AttributeList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
  position: relative;
  background: rgba(248, 250, 253, 0.5);
  padding: 1rem;
  border-radius: 8px;
  overflow: hidden;
  max-height: ${props => props.expanded ? '500px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  transition: all 0.3s ease-in-out;
  
  ${props => !props.expanded && `
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 0;
  `}
`;

const AttributeToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  color: var(--primary-color);
  background: transparent;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  margin-top: 1rem;
  font-weight: 500;
  
  &:hover {
    color: var(--primary-hover);
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const AttributeItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: var(--text-sm);
  transition: all 0.3s ease;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(240, 249, 255, 0.7);
    transform: translateX(3px);
  }
`;

const AttributeName = styled.span`
  color: var(--gray-600);
  position: relative;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: var(--primary-color);
    margin-right: 0.5rem;
    border-radius: 50%;
    opacity: 0.7;
  }
`;

const AttributeValue = styled.span`
  color: var(--gray-900);
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: var(--primary-color);
    transition: width 0.3s ease;
    opacity: 0.5;
  }
  
  ${AttributeItem}:hover &::after {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  min-height: 300px;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: var(--gray-300);
  margin-bottom: 1.5rem;
`;

const EmptyStateTitle = styled.h3`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.75rem;
`;

const EmptyStateText = styled.p`
  color: var(--gray-500);
  max-width: 300px;
  margin: 0 auto;
`;

const SummarySection = styled.div`
  background: white;
  padding: 1.5rem;
  margin: 0.75rem 0.75rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.isTotal ? '0' : '0.75rem'};
  font-size: ${props => props.isTotal ? 'var(--text-lg)' : 'var(--text-md)'};
  font-weight: ${props => props.isTotal ? '700' : '400'};
  color: ${props => props.isTotal ? 'var(--secondary-color)' : 'var(--gray-700)'};
  padding: ${props => props.isTotal ? '1rem 1.25rem' : '0'};
  border-radius: ${props => props.isTotal ? '8px' : '0'};
  
  ${props => props.isTotal && `
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f1f5f9;
    }
  `}
`;

const ActionBar = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  gap: 1.25rem;
  background: white;
  margin: 0.75rem 0.75rem 0.75rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    margin: 0.75rem 0.5rem 0.75rem;
  }

  @media (max-width: 1200px) {
    flex-wrap: wrap;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    gap: 0.875rem;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  transition: all 0.2s ease;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  min-height: 44px;

  @media (max-width: 1200px) {
    font-size: 0.85rem;
    padding: 0.7rem 1.2rem;
    gap: 0.6rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.875rem 1.3rem;
    gap: 0.7rem;
    flex: none;
    width: 100%;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    min-height: 48px;
    padding: 0.875rem 1.125rem;
  }

  ${props => props.primary && `
    background: #FF8F00;
    color: white;
    border: none;

    &:hover {
      background: #F57C00;
    }

    &:active {
      background: #E65100;
    }
  `}

  ${props => !props.primary && `
    background: #1565C0;
    color: white;
    border: none;

    &:hover {
      background: #0D47A1;
    }

    &:active {
      background: #01579B;
    }
  `}
`;

const ButtonIcon = styled.span`
  font-size: 1.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  @media (max-width: 1200px) {
    font-size: 1.1em;
  }
  
  svg {
    width: 20px;
    height: 20px;
    
    @media (max-width: 1200px) {
      width: 18px;
      height: 18px;
    }
  }
`;

// Modal components for client name input
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  width: 400px;
  max-width: 90vw;
  overflow: hidden;
  border: 1px solid rgba(52, 152, 219, 0.1);

  @media (max-width: 480px) {
    max-width: 95vw;
    border-radius: 10px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 60%);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ModalTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  
  span {
    background: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ModalBody = styled.div`
  padding: 1.75rem;
  background: #F8FAFD;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--gray-700);
  position: relative;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: var(--primary-color);
    margin-right: 0.5rem;
    border-radius: 50%;
    opacity: 0.7;
  }
  
  &::after {
    content: ${props => props.showRequired ? "'*'" : "''"}; 
    color: #EF4444;
    font-weight: bold;
    margin-left: 0.25rem;
    font-size: 1.1em;
    opacity: ${props => props.showRequired ? '1' : '0'};
    transition: opacity 0.2s ease;
  }
`;

const Input = styled.input`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.hasError ? '#EF4444' : 'rgba(52, 152, 219, 0.15)'};
  font-size: var(--text-md);
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
  background-color: ${props => props.hasError ? 'rgba(239, 68, 68, 0.05)' : 'white'};

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#EF4444' : 'var(--primary-color)'};
    box-shadow: 0 6px 12px ${props => props.hasError ? 'rgba(239, 68, 68, 0.15)' : 'rgba(52, 152, 219, 0.1)'};
  }

  ${props => props.hasError && `
    &::placeholder {
      color: rgba(239, 68, 68, 0.6);
    }
  `}

  ${props => props.readOnly && `
    background: #f8fafc !important;
    color: var(--gray-800);
    border-color: #e2e8f0;
    font-weight: 500;

    &:focus {
      border-color: #cbd5e1;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
    }
  `}

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.875rem 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 0.75rem 0.75rem;
  }
`;

const Textarea = styled.textarea`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.hasError ? '#EF4444' : 'rgba(52, 152, 219, 0.15)'};
  font-size: var(--text-md);
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
  background-color: ${props => props.hasError ? 'rgba(239, 68, 68, 0.05)' : 'white'};
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#EF4444' : 'var(--primary-color)'};
    box-shadow: 0 6px 12px ${props => props.hasError ? 'rgba(239, 68, 68, 0.15)' : 'rgba(52, 152, 219, 0.1)'};
  }

  ${props => props.hasError && `
    &::placeholder {
      color: rgba(239, 68, 68, 0.6);
    }
  `}

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.875rem 0.875rem;
    min-height: 90px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 0.75rem 0.75rem;
    min-height: 80px;
  }
`;

const ModalFooter = styled.div`
  padding: 1.25rem 1.5rem;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: var(--text-sm);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  min-height: 44px;

  ${props => props.primary && !props.disabled && `
    background: #FF8F00;
    color: white;
    border: none;
    box-shadow: 0 4px 8px rgba(255, 143, 0, 0.2);

    &:hover {
      background: #F57C00;
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(255, 143, 0, 0.3);
    }

    &:active {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(255, 143, 0, 0.25);
    }
  `}

  ${props => props.primary && props.disabled && `
    background: #d1d5db;
    color: #9ca3af;
    border: none;
    box-shadow: none;

    &:hover {
      background: #d1d5db;
      transform: none;
      box-shadow: none;
    }
  `}

  ${props => !props.primary && `
    background: white;
    color: var(--secondary-color);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.03);

    &:hover {
      background: #F5F9FF;
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
      border-color: rgba(52, 152, 219, 0.2);
    }

    &:active {
      transform: translateY(-1px);
    }
  `}

  @media (max-width: 768px) {
    min-height: 48px;
    padding: 0.875rem 1.125rem;
  }

  @media (max-width: 480px) {
    min-height: 48px;
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  color: #EF4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  font-weight: 500;
`;

const ToastNotification = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  background-color: ${props => props.success ? '#10B981' : '#EF4444'};
  color: white;
  border-radius: 12px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 300px;
  z-index: 110;
  border: 1px solid ${props => props.success ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)'};
  
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    color: ${props => props.success ? '#10B981' : '#EF4444'};
    font-weight: bold;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  }
`;

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Generate a consistent quote reference number using European date format (DD-MM-YY)
const generateReference = () => {
  const prefix = 'Q';
  const today = new Date();
  // Format: Q-DAY-MONTH-YEAR-SEQUENCE
  // Day, month, and year as 2 digits each (European format)
  const day = today.getDate().toString().padStart(2, '0');
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear().toString().slice(-2);
  
  // Use a timestamp for sequence to ensure uniqueness - just the last 4 digits
  // This will help avoid collisions if multiple quotes are generated at the same second
  const sequence = Date.now().toString().slice(-4);
  
  return `${prefix}${day}${month}${year}-${sequence}`;
};

// Format a quote reference for display, adding visual separators
export const formatQuoteReference = (reference) => {
  // If it's not in the expected format, return it as is
  if (!reference || !reference.startsWith('Q') || reference.length !== 11) {
    return reference;
  }
  
  // Format: Q-DD-MM-YY-SEQUENCE to make it more readable
  // Example: Q24032024-7891 becomes Q-24-03-24-7891
  const day = reference.substring(1, 3);
  const month = reference.substring(3, 5);
  const year = reference.substring(5, 7);
  const sequence = reference.substring(8);
  
  return `Q-${day}-${month}-${year}-${sequence}`;
};

// Map attribute names to human-readable labels
const attributeLabels = {
  poleType: 'Pole Type',
  material: 'Material',
  thickness: 'Thickness',
  size: 'Size',
  lengthRange: 'Length Range',
  type: 'Type',
  grade: 'Grade',
  sides: 'Sides',
  slide: 'Slide',
  addonType: 'Add-on Type',
  heightRange: 'Height Range',
  width: 'Width',
  length: 'Length',
  height: 'Height',
  loopLength: 'Loop Length'
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const productVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Incognito Mode Toggle Component
const IncognitoModeToggle = ({ displayMode, priceMode, incognitoExpanded, setIncognitoExpanded, onDisplayModeChange, onPriceModeChange, onIncognitoToggle }) => {
  const isIncognito = displayMode === 'incognito';
  const collapsed = isIncognito && !incognitoExpanded;

  const handleModeClick = (mode) => {
    if (mode === 'incognito') {
      if (isIncognito && collapsed) {
        // When clicking the collapsed incognito button, immediately revert to previous mode
        onIncognitoToggle();
      } else if (isIncognito && incognitoExpanded) {
        // When clicking incognito in expanded state, collapse it
        setIncognitoExpanded(false);
      } else if (isIncognito && !incognitoExpanded) {
        // When clicking incognito in collapsed but not fully collapsed state, expand
        setIncognitoExpanded(true);
      } else {
        // Switch to incognito and collapse
        onDisplayModeChange('incognito');
        setIncognitoExpanded(false);
      }
    } else {
      // Switch to partner/client mode and sync with store
      onDisplayModeChange(mode);
      if (priceMode !== mode) {
        onPriceModeChange();
      }
      setIncognitoExpanded(false);
    }
  };

  if (collapsed) {
    return (
      <IncognitoToggleContainer collapsed>
        <IncognitoButton 
          collapsed 
          active={true}
          onClick={() => handleModeClick('incognito')}
        >
          🔒
        </IncognitoButton>
      </IncognitoToggleContainer>
    );
  }

  return (
    <IncognitoToggleContainer>
      <ExpandableToggle
        initial={false}
        animate={{ width: 'auto' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <IncognitoModeOption
          active={displayMode === 'partner'}
          mode="partner"
          onClick={() => handleModeClick('partner')}
        >
          Partner
        </IncognitoModeOption>
        <IncognitoModeOption
          active={displayMode === 'client'}
          mode="client"
          onClick={() => handleModeClick('client')}
        >
          Client
        </IncognitoModeOption>
        <IncognitoModeOption
          active={displayMode === 'incognito'}
          mode="incognito"
          onClick={() => handleModeClick('incognito')}
        >
          🔒 Incognito
        </IncognitoModeOption>
      </ExpandableToggle>
    </IncognitoToggleContainer>
  );
};

function QuotePreview() {
  const [quoteReference] = useState(generateReference());
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastSuccess, setToastSuccess] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const quoteCardRef = useRef(null);
  const screenshotAreaRef = useRef(null);

  const { user } = useAuthStore();
  const [clientEmail, setClientEmail] = useState('');

  // Get email from localStorage (where landing page stores it)
  useEffect(() => {
    const storedEmail = localStorage.getItem('indus_user_email') || user?.email || '';
    setClientEmail(storedEmail);
  }, [user]);
  const {
    selectedMainCategory,
    selectedProduct,
    selectedAttributes,
    organizedProducts,
    quote,
    mainCategories,
    cartItems,
    removeFromQuote,
    priceMode,
    togglePriceMode,
    fairModeEnabled,
    recentCopySuccess,
    clearCopySuccess,
    discountEnabled,
    discountPercentage,
    setDiscountPercentage,
    clearDiscount
  } = useConfiguratorStore();
  
  const [displayMode, setDisplayMode] = useState(priceMode); // 'partner', 'client', or 'incognito'
  const [incognitoExpanded, setIncognitoExpanded] = useState(false); // For collapsible UI
  const [previousMode, setPreviousMode] = useState(priceMode); // Remember mode before incognito
  
  // Toggle expanded state of product attributes
  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  
  // Calculate totals with discount
  const getTotals = () => {
    const productSubtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);

    // Calculate discount
    const discountAmount = discountEnabled && discountPercentage > 0
      ? productSubtotal * (discountPercentage / 100)
      : 0;
    const subtotalAfterDiscount = productSubtotal - discountAmount;
    const total = subtotalAfterDiscount;

    return {
      productSubtotal,
      discountAmount,
      discountPercentage,
      subtotalAfterDiscount,
      total
    };
  };

  // Helper function to reset form state
  const resetFormState = () => {
    setPhoneNumber('');
    setAdditionalNotes('');
    setIsModalOpen(false);
  };
  
  // Function to generate the quote text body (used for email and copy)
  const generateQuoteBodyText = () => {
    const {
      cartItems,
      selectedMainCategory,
      selectedProduct,
      organizedProducts,
      selectedAttributes,
      mainCategories,
      quote,
    } = useConfiguratorStore.getState(); // Get latest state

    const formattedDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    let body = `-----------------------------------------\n\n`;
    body += `QUOTE SUMMARY (#${formatQuoteReference(quoteReference)} - ${formattedDate}):\n\n`;

    if (cartItems.length > 0) {
      cartItems.forEach((item, index) => {
        const categoryName = mainCategories.find(cat => cat.id === item.category)?.name || 'Product';
        let productNameDisplay = item.product.name;
        const topAttributes = ['type', 'material', 'sides'].filter(attr => item.attributes && item.attributes[attr]);
        if (topAttributes.length > 0) {
          const attributeString = topAttributes.map(attr => item.attributes[attr]).join(' | ');
          productNameDisplay += ` | ${attributeString}`;
        }
        body += `ITEM ${index + 1}: ${productNameDisplay}\n`;
        body += `Category: ${categoryName}\n`;
        body += `Quantity: ${item.quantity} units × ${formatCurrency(item.unitPrice)}\n`;
        if (item.attributes && Object.keys(item.attributes).length > 0) {
          body += `Specifications:\n`;
          Object.entries(item.attributes).forEach(([key, value]) => {
            body += `- ${attributeLabels[key] || key}: ${value}\n`;
          });
        }
        body += `Subtotal: ${formatCurrency(item.totalPrice)}\n\n`;
      });
    } else if (selectedProduct && organizedProducts[selectedMainCategory]) {
      const product = organizedProducts[selectedMainCategory].find(p => p.id === selectedProduct);
      const categoryName = mainCategories.find(cat => cat.id === selectedMainCategory)?.name || 'Product';
      if (product) {
        let productNameDisplay = product.name;
        const topAttributes = ['type', 'material', 'sides'].filter(attr => selectedAttributes && selectedAttributes[attr]);
        if (topAttributes.length > 0) {
          const attributeString = topAttributes.map(attr => selectedAttributes[attr]).join(' | ');
          productNameDisplay += ` | ${attributeString}`;
        }
        body += `ITEM 1: ${productNameDisplay}\n`;
        body += `Category: ${categoryName}\n`;
        body += `Quantity: ${quote.lineItems[0]?.quantity || 0} units × ${formatCurrency(quote.lineItems[0]?.price || 0)}\n`;
        if (selectedAttributes && Object.keys(selectedAttributes).length > 0) {
          body += `Specifications:\n`;
          Object.entries(selectedAttributes).forEach(([key, value]) => {
            body += `- ${attributeLabels[key] || key}: ${value}\n`;
          });
        }
        body += `Subtotal: ${formatCurrency(quote.subtotal)}\n\n`;
      }
    }

    const totals = getTotals();
    body += `-----------------------------------------\n`;
    body += `SUBTOTAL (Products): ${formatCurrency(totals.productSubtotal)}\n`;
    body += `TOTAL AMOUNT: ${formatCurrency(totals.total)}\n`;
    body += `-----------------------------------------\n`;
    body += `TERMS AND CONDITIONS:\n`;
    body += `- Quote valid for 30 days from the date of issue\n`;
    body += `- Delivery time: 2-3 weeks from order confirmation\n`;
    body += `- Prices are in EUR and exclude VAT\n`;

    return body;
  };


  // Function to open client info modal for sending a quote
  const handleSendQuoteClick = () => {
    setIsModalOpen(true);
  };

  // Function to save quote request to database
  const handleSendQuoteRequest = async () => {
    // Validate that user email exists
    if (!clientEmail) {
      setToastMessage('User email not found. Please log in again.');
      setToastSuccess(false);
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 3000);
      return;
    }

    try {
      // Prepare items data with complete product information
      const quoteItems = cartItems.map(item => ({
        id: item.id,
        product_id: item.product.id,
        name: item.product.name,
        category: item.category,
        category_name: mainCategories.find(cat => cat.id === item.category)?.name || 'Unknown',
        attributes: item.attributes || {},
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.totalPrice,
        tier_name: item.tierName || null,
        calculated_length: item.calculatedLength || null
      }));

      // Calculate totals
      const totals = getTotals();

      // Insert the quote request to Supabase
      const { error } = await supabase
        .from('quote_requests')
        .insert({
          reference: quoteReference,
          user_id: user?.id || null,
          user_email: clientEmail,
          client_email: clientEmail,
          phone_number: phoneNumber.trim() || null,
          additional_notes: additionalNotes.trim() || null,
          items: quoteItems,
          discount_enabled: discountEnabled,
          discount_percentage: discountEnabled ? discountPercentage : null,
          discount_amount: discountEnabled ? totals.discountAmount : null,
          subtotal: totals.productSubtotal,
          total_amount: totals.total,
          status: 'pending'
        });

      if (error) {
        throw error;
      }

      // Reset form and close modal
      resetFormState();

      // Success message
      setToastMessage('Quote request submitted successfully!');
      setToastSuccess(true);
      setToastVisible(true);

      setTimeout(() => {
        setToastVisible(false);
      }, 3000);

    } catch (error) {
      console.error('Error saving quote request:', error);

      // Show error message
      setToastMessage('Failed to submit quote request. Please try again.');
      setToastSuccess(false);
      setToastVisible(true);

      setTimeout(() => {
        setToastVisible(false);
      }, 3000);
    }
  };
  

  
  // Monitor cartItems for changes to trigger highlight animation
  useEffect(() => {
    if (cartItems.length > 0) {
      // Trigger highlight effect
      setIsHighlighted(true);
      
      // Reset highlight after animation completes
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [cartItems.length, JSON.stringify(cartItems)]);

  // Handle success notification for copied items
  useEffect(() => {
    if (recentCopySuccess && recentCopySuccess.timestamp) {
      // Only show notification if the copy was recent (within the last 5 seconds)
      const timeSinceSuccess = Date.now() - recentCopySuccess.timestamp;
      if (timeSinceSuccess < 5000) {
        setShowSuccessNotification(true);
        
        // Auto-hide notification after 4 seconds
        const timer = setTimeout(() => {
          setShowSuccessNotification(false);
          // Clear the success state after hiding the notification
          setTimeout(() => clearCopySuccess(), 300);
        }, 4000);
        
        return () => clearTimeout(timer);
      } else {
        // Clear old success state
        clearCopySuccess();
      }
    }
  }, [recentCopySuccess, clearCopySuccess]);

  // Sync displayMode with store's priceMode when store changes
  useEffect(() => {
    // Only sync if displayMode is not incognito (incognito is a local preference)
    if (displayMode !== 'incognito' && displayMode !== priceMode) {
      setDisplayMode(priceMode);
      setPreviousMode(priceMode);
    }
  }, [priceMode, displayMode]);

  // Enhanced handlers for incognito toggle with previous mode tracking
  const handleDisplayModeChange = (newMode) => {
    if (newMode === 'incognito') {
      // Store current mode before switching to incognito
      if (displayMode !== 'incognito') {
        setPreviousMode(displayMode);
      }
      setDisplayMode('incognito');
    } else {
      // Switching to partner or client mode
      setDisplayMode(newMode);
      setPreviousMode(newMode);
    }
  };

  const handleIncognitoToggle = () => {
    if (displayMode === 'incognito') {
      // Revert to previous mode when opening incognito toggle
      setDisplayMode(previousMode);
      if (priceMode !== previousMode) {
        togglePriceMode();
      }
    } else {
      // This shouldn't happen with current logic, but handle gracefully
      handleDisplayModeChange('incognito');
    }
  };
  
  // Get the selected product object if one is selected
  return (
    <React.Fragment>
      <PreviewContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        
      <QuoteContainer>
        <AnimatePresence mode="wait">
          {cartItems.length === 0 ? (
            <QuoteCard
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <EmptyState>
                <EmptyStateIcon>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="21" r="1"></circle>
                    <circle cx="19" cy="21" r="1"></circle>
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                  </svg>
                </EmptyStateIcon>
                <EmptyStateTitle>Your quote is empty</EmptyStateTitle>
                <EmptyStateText>
                  Configure a product using the steps on the left, then click "Add to Quote" to see it here.
                </EmptyStateText>
              </EmptyState>
            </QuoteCard>
          ) : (
            <QuoteCard
              key="filled"
              ref={quoteCardRef}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{
                boxShadow: 'var(--shadow-md)'
              }}
              transition={{ duration: 0.3 }}
            >
              <div ref={screenshotAreaRef}>
                <QuoteHeader>
                <QuoteTitleBar>
                  <QuoteIcon>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                  </QuoteIcon>
                  <QuoteReference>{formatQuoteReference(quoteReference)}</QuoteReference>
                </QuoteTitleBar>
              </QuoteHeader>
              
              
              <ProductDetails>
                {cartItems.map((item, index) => (
                  <ProductItem
                    key={item.id || index}
                    variants={productVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <ProductHeader>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        <div style={{ 
                          width: '38px', 
                          height: '38px', 
                          borderRadius: 'var(--rounded-md)',
                          border: '1px solid var(--gray-200)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'white',
                          overflow: 'hidden'
                        }}>
                          {(() => {
                            const productData = item.product; // Data source for cart items
                            const imgSrc = getProductImageSrc(productData, item.category); // Pass item.category as mainCategoryId
                            const productName = productData?.name || 'Product';
                            const isUrl = typeof imgSrc === 'string' && imgSrc.startsWith('http');
                            const style = { width: '100%', height: '100%', objectFit: 'contain' };
                            // Apply specific scaling for the Stainless Steel Feet Sets

                            // Apply specific scaling for certain spare parts
                            if (productData?.category === 'spare_parts') {
                              const nameLower = productName.toLowerCase();
                              // Apply specific scaling if the image source is the target S.S. Feet Set image
                              if (imgSrc === 'https://i.imgur.com/Hg5jDU9.png') {
                                style.transform = 'scale(1.1)'; // 10% larger
                              }

                              if ((nameLower.includes('rolling springhead') && nameLower.includes('roll')) ||
                                  (nameLower.includes('springhead') && nameLower.includes('replacement'))) {
                                style.transform = 'scale(1.3)';
                              }
                            }

                            return isUrl ? (
                              <LazyImage src={imgSrc} alt={productName} style={style} />
                            ) : (
                              <span style={{ fontSize: '20px' }}>{imgSrc}</span>
                            );
                          })()}
                        </div>
                        <ProductName>{item.product.name}</ProductName>
                      </div>
                      <ProductPrice>
                        {formatCurrency(item.totalPrice || (item.unitPrice * item.quantity))}
                      </ProductPrice>
                    </ProductHeader>

                    <ProductMeta>
                      <MetaItem>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        #{item.product.id}
                      </MetaItem>
                      <MetaItem>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                        </svg>
                        {item.category && mainCategories.find(cat => cat.id === item.category)?.name}
                      </MetaItem>
                      {item.calculatedLength && item.category === 'poles' && (
                        <CalculatedLengthBadge>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                          </svg>
                          {item.calculatedLength}
                        </CalculatedLengthBadge>
                      )}
                      <QuantityTag>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                          <line x1="6" y1="6" x2="6.01" y2="6"></line>
                          <line x1="6" y1="18" x2="6.01" y2="18"></line>
                        </svg>
                        {item.quantity}×{formatCurrency(item.unitPrice)}
                        {item.tierName && (
                          <span style={{ fontSize: '0.9em', marginLeft: '2px', opacity: 0.8 }}>
                            ({item.tierName})
                          </span>
                        )}
                      </QuantityTag>
                      <RemoveButton
                        onClick={() => removeFromQuote(item.id)}
                        title="Remove item"
                        aria-label="Remove item"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </RemoveButton>
                    </ProductMeta>

                    {item.attributes && Object.keys(item.attributes).length > 0 && (
                      <>
                        <AttributeToggle
                          expanded={expandedItems[item.id]}
                          onClick={() => toggleExpanded(item.id)}
                        >
                          {expandedItems[item.id] ? 'Hide Specifications' : 'View Specifications'}
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </AttributeToggle>

                        <AttributeList expanded={expandedItems[item.id]}>
                          {Object.entries(item.attributes).map(([key, value], attrIndex) => (
                            <AttributeItem key={attrIndex}>
                              <AttributeName>{attributeLabels[key] || key}</AttributeName>
                              <AttributeValue>{value}</AttributeValue>
                            </AttributeItem>
                          ))}
                        </AttributeList>
                      </>
                    )}
                  </ProductItem>
                ))}
              </ProductDetails>

              {/* Discount Section - only show if enabled in settings */}
              {discountEnabled && (
                <DiscountSection
                  productSubtotal={cartItems.reduce((sum, item) => sum + item.totalPrice, 0)}
                  currentPercentage={discountPercentage}
                  onDiscountApplied={setDiscountPercentage}
                  onDiscountCleared={clearDiscount}
                />
              )}

              <SummarySection>
                {(() => {
                  const totals = getTotals();

                  return (
                    <>
                      {/* Show different layouts based on whether discount is present */}
                      {totals.discountAmount > 0 ? (
                        // When discount is present, show breakdown
                        <>
                          <SummaryRow>
                            <div>Products ({cartItems.length > 0 ? cartItems.length : (quote.lineItems.length > 0 ? 1 : 0)} item{(cartItems.length > 0 ? cartItems.length : (quote.lineItems.length > 0 ? 1 : 0)) !== 1 ? 's' : ''})</div>
                            <div>{formatCurrency(totals.productSubtotal)}</div>
                          </SummaryRow>

                          {/* Discount Row - only show if discount is applied */}
                          {totals.discountAmount > 0 && (
                            <SummaryRow style={{ color: '#10b981' }}>
                              <div>Discount ({totals.discountPercentage}%)</div>
                              <div>- {formatCurrency(totals.discountAmount)}</div>
                            </SummaryRow>
                          )}

                          <SummaryRow isTotal>
                            <div>Total</div>
                            <div>{formatCurrency(totals.total)}</div>
                          </SummaryRow>
                        </>
                      ) : (
                        // When no discount, show simple format
                        cartItems.length > 0 ? (
                          cartItems.length === 1 ? (
                            <SummaryRow isTotal>
                              <div>Subtotal ({cartItems[0].quantity} × {formatCurrency(cartItems[0].unitPrice)})</div>
                              <div>{formatCurrency(cartItems[0].totalPrice)}</div>
                            </SummaryRow>
                          ) : (
                            <SummaryRow isTotal>
                              <div>Total ({cartItems.length} items)</div>
                              <div>{formatCurrency(totals.productSubtotal)}</div>
                            </SummaryRow>
                          )
                        ) : quote.lineItems.length > 0 && (
                          <SummaryRow isTotal>
                            <div>Subtotal ({quote.lineItems[0].quantity} × {formatCurrency(quote.lineItems[0].price)})</div>
                            <div>{formatCurrency(quote.lineItems[0].totalPrice)}</div>
                          </SummaryRow>
                        )
                      )}
                    </>
                  );
                })()}
              </SummarySection>
              </div>
              
              <ActionBar>
                 {/* Send Request Button */}
                 <ActionButton
                   primary={true}
                   whileTap={{ scale: 0.98 }}
                   onClick={handleSendQuoteClick}
                   style={{
                     width: '100%',
                     fontSize: '1rem',
                     padding: '1rem 2rem',
                     justifyContent: 'center'
                   }}
                 >
                   <ButtonIcon>
                     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                       <line x1="22" y1="2" x2="11" y2="13"></line>
                       <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                     </svg>
                   </ButtonIcon>
                   Send Request
                 </ActionButton>
               </ActionBar>
            </QuoteCard>
          )}
        </AnimatePresence>
      </QuoteContainer>
    </PreviewContainer>,
    
    {/* Client Info Modal */}
    <AnimatePresence>
      {isModalOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            onClick={e => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <span>📤</span>
                Send Quote Request
              </ModalTitle>
            </ModalHeader>
            
            <ModalBody>
              <ModalForm onSubmit={(e) => e.preventDefault()}>
                <FormGroup>
                  <Label htmlFor="clientEmail">Your Email</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={clientEmail}
                    readOnly
                    style={{ cursor: 'not-allowed' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phoneNumber">Phone Number (Optional)</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="Enter phone number"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    autoFocus
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="additionalNotes">Additional Notes for Indus Team (Optional)</Label>
                  <Textarea
                    id="additionalNotes"
                    placeholder="Add any special requests or notes for the Indus team..."
                    value={additionalNotes}
                    onChange={e => setAdditionalNotes(e.target.value)}
                    rows={4}
                  />
                </FormGroup>
              </ModalForm>
            </ModalBody>
            
            <ModalFooter>
              <ModalButton onClick={() => resetFormState()}>
                Cancel
              </ModalButton>
               <ModalButton
                 primary
                 onClick={handleSendQuoteRequest}
                 disabled={!clientEmail}
               >
                 Send Request
               </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
    
    {/* Toast notification */}
    <AnimatePresence>
      {toastVisible && (
        <ToastNotification
          success={toastSuccess}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
        >
          <span>{toastSuccess ? '✅' : '❌'}</span>
          {toastMessage}
        </ToastNotification>
      )}
    </AnimatePresence>

    {/* Success Notification for Copied Items */}
    <AnimatePresence>
      {showSuccessNotification && recentCopySuccess && (
        <SuccessNotification
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <SuccessIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13L9 17L19 7"/>
            </svg>
          </SuccessIcon>
          <SuccessContent>
            <SuccessTitle>Items Successfully Copied!</SuccessTitle>
            <SuccessDetails>
              {recentCopySuccess.copiedCount === recentCopySuccess.totalCount 
                ? `All ${recentCopySuccess.copiedCount} item${recentCopySuccess.copiedCount !== 1 ? 's' : ''} copied to quote`
                : `${recentCopySuccess.copiedCount} of ${recentCopySuccess.totalCount} items copied`
              }
            </SuccessDetails>
          </SuccessContent>
        </SuccessNotification>
      )}
    </AnimatePresence>
    </React.Fragment>
  );
}

export default QuotePreview;
